import React, { useRef, useState, useEffect, useCallback, useMemo } from "react";
import "./ShowcaseSection.css";

// Throttle function to limit the rate of function execution
const throttle = (func, limit) => {
  let inThrottle;
  return function(...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
};

// Video data with captions and years
const videoFiles = [
  {
    src: "/assets/dailydrawingvids/3371170128223832344.mp4",
    caption: "Daily Drawing #1",
    year: "2024"
  },
  {
    src: "/assets/dailydrawingvids/3372552845523002571.mp4",
    caption: "Daily Drawing #2",
    year: "2024"
  },
  {
    src: "/assets/dailydrawingvids/3385004268758086453.mp4",
    caption: "Daily Drawing #3",
    year: "2024"
  },
  {
    src: "/assets/dailydrawingvids/3392170492418927276.mp4",
    caption: "Daily Drawing #4",
    year: "2024"
  },
  {
    src: "/assets/dailydrawingvids/3403015513120687691.mp4",
    caption: "Daily Drawing #5",
    year: "2024"
  },
  {
    src: "/assets/dailydrawingvids/3417543049678462764.mp4",
    caption: "Daily Drawing #6",
    year: "2024"
  },
  {
    src: "/assets/dailydrawingvids/3426248279840614405.mp4",
    caption: "Daily Drawing #7",
    year: "2024"
  },
  {
    src: "/assets/dailydrawingvids/3447705300402837325.mp4",
    caption: "Daily Drawing #8",
    year: "2024"
  },
  {
    src: "/assets/dailydrawingvids/3455187146652128186.mp4",
    caption: "Daily Drawing #9",
    year: "2024"
  },
  {
    src: "/assets/dailydrawingvids/3467577364013369837.mp4",
    caption: "Daily Drawing #10",
    year: "2024"
  },
  {
    src: "/assets/dailydrawingvids/3472582954418497366.mp4",
    caption: "Daily Drawing #11",
    year: "2024"
  },
  {
    src: "/assets/dailydrawingvids/3477660625179690036.mp4",
    caption: "Daily Drawing #12",
    year: "2024"
  },
  {
    src: "/assets/dailydrawingvids/3498728778001133357.mp4",
    caption: "Daily Drawing #13",
    year: "2024"
  },
  {
    src: "/assets/dailydrawingvids/3524077152875137195.mp4",
    caption: "Daily Drawing #14",
    year: "2024"
  },
  {
    src: "/assets/dailydrawingvids/3545118898929066111.mp4",
    caption: "Daily Drawing #15",
    year: "2024"
  },
  {
    src: "/assets/dailydrawingvids/3577023728412860223.mp4",
    caption: "Daily Drawing #16",
    year: "2024"
  },
  {
    src: "/assets/dailydrawingvids/3667295870631377480.mp4",
    caption: "Daily Drawing #17",
    year: "2024"
  },
  {
    src: "/assets/dailydrawingvids/3672280980878761150.mp4",
    caption: "Daily Drawing #18",
    year: "2024"
  }
];

const VideoItem = React.memo(({ src, caption, year, isVisible }) => {
  const videoRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const itemRef = useRef(null);
  const observer = useRef(null);

  // Directly respond to isVisible prop (active item changes)
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (isVisible) {
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  }, [isVisible]);

  // Intersection Observer — auto-play when visible
  useEffect(() => {
    const video = videoRef.current;
    const item  = itemRef.current;
    if (!video || !item) return;

    const handleIntersection = (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          if (isVisible) {
            video.play().catch(() => {});
          }
        } else {
          video.pause();
        }
      });
    };

    const obs = new IntersectionObserver(handleIntersection, {
      root: null,
      rootMargin: '0px',
      threshold: 0.5
    });
    obs.observe(item);
    observer.current = obs;

    return () => {
      obs.unobserve(item);
      obs.disconnect();
      video.pause();
      video.src = '';
      video.load();
    };
  }, [isVisible]);

  // Handle video loading state
  const handleLoadedData = () => setIsLoading(false);

  return (
    <div
      ref={itemRef}
      className={`showcase-gallery-item ${isVisible ? 'is-visible' : ''} ${isLoading ? 'is-loading' : ''}`}
      data-visible={isVisible}
    >
      <div className="showcase-gallery-video-container">
        {isLoading && (
          <div className="video-loading">
            <div className="spinner"></div>
          </div>
        )}
        <video
          ref={videoRef}
          className="showcase-gallery-video"
          loop
          muted
          playsInline
          preload="metadata"
          onLoadedData={handleLoadedData}
          onClick={(e) => {
            e.preventDefault();
            const v = e.currentTarget;
            if (v.paused) { v.play().catch(() => {}); } else { v.pause(); }
          }}
        >
          <source src={src} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      </div>
      <div className="showcase-gallery-caption-wrap">
        <span className="showcase-gallery-caption accent">{caption}</span>
        <span className="showcase-gallery-caption year"> — {year}</span>
      </div>
    </div>
  );
});


const ShowcaseSection = () => {
  const scrollRef = useRef(null);
  const isDownRef = useRef(false);
  const startXRef = useRef(0);
  const scrollLeftRef = useRef(0);
  const [visibleIndex, setVisibleIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const rafId = useRef(null);

  // Check for mobile devices
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  // Handle scroll events for visibility detection with throttling
  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer) return;

    const handleScroll = () => {
      if (rafId.current) {
        cancelAnimationFrame(rafId.current);
      }

      rafId.current = requestAnimationFrame(() => {
        const scrollPosition = scrollContainer.scrollLeft + (scrollContainer.offsetWidth / 2);
        const items = scrollContainer.querySelectorAll('.showcase-gallery-item');
        
        let closestItem = { index: 0, distance: Infinity };
        
        items.forEach((item, index) => {
          const itemRect = item.getBoundingClientRect();
          const containerRect = scrollContainer.getBoundingClientRect();
          const itemCenter = itemRect.left + (itemRect.width / 2) - containerRect.left + scrollContainer.scrollLeft;
          const distance = Math.abs(scrollPosition - itemCenter);
          
          if (distance < closestItem.distance) {
            closestItem = { index, distance };
          }
        });
        
        setVisibleIndex(closestItem.index);
      });
    };

    const throttledScroll = throttle(handleScroll, 50);
    scrollContainer.addEventListener('scroll', throttledScroll, { passive: true });
    
    // Initial check with a small delay to ensure DOM is ready
    const initTimer = setTimeout(handleScroll, 100);
    
    return () => {
      scrollContainer.removeEventListener('scroll', throttledScroll);
      clearTimeout(initTimer);
      if (rafId.current) {
        cancelAnimationFrame(rafId.current);
      }
    };
  }, []);

  // Handle scroll to center the active item
  const scrollToIndex = useCallback((index) => {
    const container = scrollRef.current;
    if (!container) return;
    
    const items = container.querySelectorAll('.showcase-gallery-item');
    if (!items[index]) return;
    
    const item = items[index];
    const containerWidth = container.offsetWidth;
    const itemLeft = item.offsetLeft;
    const itemWidth = item.offsetWidth;
    const scrollTo = itemLeft - (containerWidth / 2) + (itemWidth / 2);
    
    container.scrollTo({
      left: scrollTo,
      behavior: 'smooth'
    });
  }, []);

  // Handle keyboard navigation
  useEffect(() => {
    if (isMobile) return;
    
    const handleKeyDown = (e) => {
      if (!scrollRef.current) return;
      
      switch(e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          scrollToIndex(Math.max(0, visibleIndex - 1));
          break;
        case 'ArrowRight':
          e.preventDefault();
          scrollToIndex(Math.min(videoFiles.length - 1, visibleIndex + 1));
          break;
        default:
          break;
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [visibleIndex, isMobile, scrollToIndex]);

  // Touch and mouse event handlers with better performance
  const handlePointerDown = useCallback((e) => {
    isDownRef.current = true;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    startXRef.current = clientX - (scrollRef.current?.getBoundingClientRect().left || 0);
    scrollLeftRef.current = scrollRef.current?.scrollLeft || 0;
    
    if (scrollRef.current) {
      scrollRef.current.style.scrollSnapType = 'none';
      scrollRef.current.style.cursor = 'grabbing';
      scrollRef.current.style.userSelect = 'none';
    }
    
    e.preventDefault();
  }, []);

  const handlePointerUp = useCallback(() => {
    isDownRef.current = false;
    if (scrollRef.current) {
      scrollRef.current.style.scrollSnapType = 'x mandatory';
      scrollRef.current.style.cursor = 'grab';
      scrollRef.current.style.userSelect = '';
    }
  }, []);

  const handlePointerMove = useCallback((e) => {
    if (!isDownRef.current || !scrollRef.current) return;
    e.preventDefault();
    
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const x = clientX - (scrollRef.current.getBoundingClientRect().left || 0);
    const walk = (x - startXRef.current) * 1.5;
    
    scrollRef.current.scrollLeft = scrollLeftRef.current - walk;
  }, []);

  // Cleanup event listeners on unmount
  useEffect(() => {
    const container = scrollRef.current;
    return () => {
      if (container) {
        container.removeEventListener('mousedown', handlePointerDown);
        container.removeEventListener('touchstart', handlePointerDown);
      }
      document.removeEventListener('mouseup', handlePointerUp);
      document.removeEventListener('touchend', handlePointerUp);
      document.removeEventListener('mousemove', handlePointerMove);
      document.removeEventListener('touchmove', handlePointerMove);
    };
  }, [handlePointerDown, handlePointerMove, handlePointerUp]);

  // Memoize the video items to prevent unnecessary re-renders
  const videoItems = useMemo(() => {
    return videoFiles.map((video, idx) => (
      <VideoItem 
        key={`${video.src}-${idx}`}
        src={video.src}
        caption={video.caption}
        year={video.year}
        isVisible={idx === visibleIndex}
      />
    ));
  }, [visibleIndex]);

  return (
    <section 
      className="showcase-section showcase-gallery-section" 
      aria-label="Daily Drawing Showcase"
      style={{ '--visible-index': visibleIndex }}
    >
      {/* Header is provided by parent section */}
      
      <div className="showcase-gallery-scroll-container">
        <div 
          ref={scrollRef}
          className="showcase-gallery-scroll"
          onMouseDown={handlePointerDown}
          onMouseUp={handlePointerUp}
          onMouseLeave={handlePointerUp}
          onTouchStart={handlePointerDown}
          onTouchEnd={handlePointerUp}
          onTouchMove={handlePointerMove}
          role="region"
          aria-label="Gallery items"
          tabIndex={0}
        >
          {videoItems}
        </div>
        
        {!isMobile && (
          <>
            <button 
              className="scroll-button scroll-button--left"
              onClick={() => scrollToIndex(Math.max(0, visibleIndex - 1))}
              aria-label="Previous item"
              disabled={visibleIndex === 0}
            >
              ←
            </button>
            <button 
              className="scroll-button scroll-button--right"
              onClick={() => scrollToIndex(Math.min(videoFiles.length - 1, visibleIndex + 1))}
              aria-label="Next item"
              disabled={visibleIndex === videoFiles.length - 1}
            >
              →
            </button>
          </>
        )}
      </div>
      
      <div className="showcase-gallery-bignum">
        {videoFiles.length} DAYS
      </div>
      
      <div className="showcase-gallery-indicators">
        {videoFiles.map((_, idx) => (
          <button
            key={idx}
            className={`indicator ${idx === visibleIndex ? 'active' : ''}`}
            onClick={() => scrollToIndex(idx)}
            aria-label={`Go to item ${idx + 1}`}
            aria-current={idx === visibleIndex}
          />
        ))}
      </div>
      
      {!isMobile && (
        <div className="showcase-gallery-scrollhint" aria-hidden="true">
          <span>← Scroll or use arrow keys to explore →</span>
        </div>
      )}
    </section>
  );
};

export default React.memo(ShowcaseSection);
